import { type FC, useCallback, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import { HoverableToolbarContext } from "./HoverableToolbarProvider";
import { DeleteIconButtonProps, ToolbarDeleteIconButton } from "./ToolbarDeleteIconButton";

import {
  AddIconButtonProps,
  ToolbarAddIconButton,
} from "components/hover-widgets/ToolbarAddIconButton";
import {
  EditIconButtonProps,
  ToolbarEditIconButton,
} from "components/hover-widgets/ToolbarEditIconButton";
import { useAppSelector } from "store/index";
import { selectIsEditModeEnabled } from "store/selectors";
import { usePresentation, useSelectedPageListItemColoring } from "utils/presentation";

type HoverableToolbarProps = {
  buttonClassName?: string;
  className?: string;
  contentClassName?: string;
  uncentered?: boolean;
  reference?: AddIconButtonProps["reference"] | EditIconButtonProps["reference"];
  readOnly?: boolean;
  label?: string;
  children: React.ReactNode;
  showEditButton?: boolean;
  size?: "xs" | "s";
  disableUnlessEditMode?: boolean;
  eventPrefix?: string;
  onClickAdd?: () => void;
  onClickDelete?: () => void;
  onEditModeChange?: (isEditing: boolean) => void;
  extraListItems?: React.ReactNode;
  extraListItemsAlwaysShown?: React.ReactNode;
};

const SELECTED_COLORING_CLASSES: Record<
  ReturnType<typeof useSelectedPageListItemColoring>,
  string
> = {
  "selected-blue": "group-data-[selected]/listitem:bg-blue",
  "selected-green": "group-data-[selected]/listitem:bg-green-shade-20",
  "selected-orange": "group-data-[selected]/listitem:bg-orange",
};

/*
 * A component used to display an editing toolbar when hovering over an element.
 * This component may need to work in conjunction with an editable field component
 * hence we use a context here to manage the editing state.
 */
export const HoverableToolbar: FC<HoverableToolbarProps> = ({
  buttonClassName,
  className,
  contentClassName,
  label,
  reference,
  readOnly,
  children,
  disableUnlessEditMode = false,
  uncentered = false,
  eventPrefix,
  onClickAdd,
  onClickDelete,
  onEditModeChange,
  showEditButton,
  size = "s",
  extraListItems,
  extraListItemsAlwaysShown,
}) => {
  const [isEditing, setIsEditingState] = useState(false);
  const [shouldStayOpen, setShouldStayOpen] = useState(false);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const shouldRenderToolbar =
    !readOnly && !isEditing && (!disableUnlessEditMode || isEditModeEnabled);

  // Using this function mainly to let callers know that we're editing
  // so they disable dragging and other functions we don't want while editing
  const setIsEditing = useCallback(
    (editing: boolean) => {
      setIsEditingState(editing);

      if (onEditModeChange) {
        onEditModeChange?.(editing);
      }
    },
    [setIsEditingState, onEditModeChange],
  );

  const contextValue = useMemo(() => {
    const setEditingIfAllowed = (editing: boolean) => {
      if (!readOnly) {
        setIsEditing(editing);
      }
    };

    return { isEditing, setIsEditing: setEditingIfAllowed, shouldStayOpen, setShouldStayOpen };
  }, [isEditing, setIsEditing, shouldStayOpen, setShouldStayOpen, readOnly]);

  const onClickEdit = () => {
    setIsEditing(true);
  };

  const { presentedPageId } = usePresentation();
  const selectedListItemColoring = useSelectedPageListItemColoring();
  const selectedColor = presentedPageId ? selectedListItemColoring : "selected-blue";

  return (
    <HoverableToolbarContext.Provider value={contextValue}>
      {/* Using role=toolbar here to represent this as an object that generates a toolbar on hover
         Mainly we use this to simulate the hovering toolbar behavior in the tests */}
      <div
        aria-label={label}
        className={twMerge(
          "group/hoverable-toolbar",
          "relative flex w-full min-w-12 items-center justify-between",
          className,
        )}
        role="toolbar"
        {...(shouldStayOpen ? { "data-stay-open": true } : {})}
      >
        {children}
        {(shouldRenderToolbar || extraListItemsAlwaysShown) && (
          <ul
            className={twMerge(
              "sr-only ml-2 flex items-center group-focus-within/hoverable-toolbar:not-sr-only group-hover/hoverable-toolbar:not-sr-only group-data-[stay-open]/hoverable-toolbar:not-sr-only",
              uncentered ? "h-[unset]" : "h-full",
              SELECTED_COLORING_CLASSES[selectedColor] || "",
              contentClassName,
            )}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {shouldRenderToolbar && onClickAdd && (
              <li>
                <ToolbarAddIconButton
                  className={buttonClassName}
                  eventPrefix={eventPrefix}
                  reference={reference as AddIconButtonProps["reference"]}
                  size={size}
                  onClick={onClickAdd}
                />
              </li>
            )}
            {shouldRenderToolbar && onClickDelete && (
              <li>
                <ToolbarDeleteIconButton
                  className={buttonClassName}
                  eventPrefix={eventPrefix}
                  reference={reference as DeleteIconButtonProps["reference"]}
                  size={size}
                  onClick={onClickDelete}
                />
              </li>
            )}
            {shouldRenderToolbar && showEditButton && (
              <li>
                <ToolbarEditIconButton
                  className={buttonClassName}
                  eventPrefix={eventPrefix}
                  reference={reference as EditIconButtonProps["reference"]}
                  size={size}
                  onClick={onClickEdit}
                />
              </li>
            )}
            {shouldRenderToolbar && extraListItems}
            {extraListItemsAlwaysShown}
          </ul>
        )}
      </div>
    </HoverableToolbarContext.Provider>
  );
};
